import * as R from 'ramda';

import {
  groupKey,
  SubItemContents,
  SubItemTreeSnapshot,
} from '@atom/components/common/subItemTree/subItemTreeReducer';
import {
  AssetAttribute,
  AssetAttributeGroup,
  AssetDetail,
  AssetElement,
  AssetElementAttribute,
  ChangeType,
} from '@atom/types/asset';

// The gateway's `workOrderAssetTree` query: the work service's
// GET /workOrders/:id/assets/:assetId/tree with the root typed and every
// nested node passed through raw. Every depth carries full attributes and a
// changeType the work service has already aggregated over the node's
// subtree. The root changeType arrives as the camelCase enum, nested ones
// keep the hyphenated wire spelling; `toChangeType` accepts both.
export type WorkOrderAssetTreeGroup = {
  id?: string;
  name: string;
  elements: WorkOrderAssetTreeNode[];
};

export type WorkOrderAssetTreeAttributeGroup = {
  id?: string;
  name: string;
  expandByDefault?: boolean;
  attributes: string[];
};

export type WorkOrderAssetTreeNode = {
  id: string;
  name: string;
  assetType: string;
  schemaId: string;
  markerId?: number;
  isNew?: boolean;
  changeType?: string | null;
  includeChangesBefore?: number;
  attributes?: Record<string, AssetAttribute>;
  attributeGroups?: WorkOrderAssetTreeAttributeGroup[];
  elements?: WorkOrderAssetTreeNode[];
  elementGroups?: WorkOrderAssetTreeGroup[];
};

export type NormalizedWorkOrderAssetTree = SubItemTreeSnapshot & {
  isNewById: Record<string, boolean>;
};

// Work-service serializes these two with hyphens; the shared tree only knows
// the camelCase enum values.
const WIRE_CHANGE_TYPES: Record<string, ChangeType> = {
  'parent-added': ChangeType.PARENT_ADDED,
  'elements-changed': ChangeType.ELEMENTS_CHANGED,
};

export const toChangeType = (wire?: string | null): ChangeType | undefined =>
  wire ? (WIRE_CHANGE_TYPES[wire] ?? (wire as ChangeType)) : undefined;

const groupId = (group: WorkOrderAssetTreeGroup): string =>
  group.id ?? group.name;

const childNodes = (node: WorkOrderAssetTreeNode): WorkOrderAssetTreeNode[] => [
  ...(node.elements ?? []),
  ...(node.elementGroups ?? []).flatMap(group => group.elements),
];

const hasDescendantChanges = (node: WorkOrderAssetTreeNode): boolean =>
  childNodes(node).some(
    child => Boolean(child.changeType) || hasDescendantChanges(child),
  );

const hydrateAttributeGroups = (
  node: WorkOrderAssetTreeNode,
): AssetAttributeGroup[] =>
  (node.attributeGroups ?? []).map(group => ({
    id: group.id,
    name: group.name,
    expandByDefault: group.expandByDefault,
    attributes: group.attributes
      .filter(attributeId => Boolean(node.attributes?.[attributeId]))
      .map(attributeId => ({
        ...node.attributes[attributeId],
        id: attributeId,
      })),
  }));

const toElementAttributes = (
  attributeGroups: AssetAttributeGroup[],
): AssetElementAttribute[] =>
  attributeGroups
    .flatMap(group => group.attributes)
    .filter(attribute => attribute.isVisibleAsSubtext)
    .map(attribute => ({
      id: attribute.id,
      name: attribute.name,
      value: attribute.value,
      unit: attribute.unit,
      dataType: attribute.dataType,
      isVisibleAsSubtext: attribute.isVisibleAsSubtext,
    }));

const toElement = (node: WorkOrderAssetTreeNode): AssetElement => ({
  id: node.id,
  name: node.name,
  assetType: node.assetType,
  schemaId: node.schemaId,
  markerId: node.markerId,
  hasChildren: !R.isEmpty(childNodes(node)),
  changeType: toChangeType(node.changeType),
  hasDescendantChanges: hasDescendantChanges(node),
  includeChangesBefore: node.includeChangesBefore,
  attributes: toElementAttributes(hydrateAttributeGroups(node)),
});

// AttributeDetails and the pane read only these fields. The REST tree has no
// inventory metadata (ancestors, budget, audit fields), so the rest of
// AssetDetail stays unset.
const toDetail = (node: WorkOrderAssetTreeNode): AssetDetail =>
  ({
    id: node.id,
    name: node.name,
    assetType: node.assetType,
    schemaId: node.schemaId,
    markerId: node.markerId,
    changeType: toChangeType(node.changeType),
    includeChangesBefore: node.includeChangesBefore,
    attributeGroups: hydrateAttributeGroups(node),
    elements: (node.elements ?? []).map(toElement),
    elementGroups: (node.elementGroups ?? []).map(group => ({
      id: groupId(group),
      name: group.name,
      elements: group.elements.map(toElement),
    })),
  }) as AssetDetail;

const toContents = (node: WorkOrderAssetTreeNode): SubItemContents => ({
  ungroupedIds: (node.elements ?? []).map(element => element.id),
  groups: (node.elementGroups ?? []).map(group => ({
    key: groupKey(node.id, groupId(group)),
    id: groupId(group),
    name: group.name,
    elementIds: group.elements.map(element => element.id),
  })),
});

const flatten = (node: WorkOrderAssetTreeNode): WorkOrderAssetTreeNode[] => [
  node,
  ...childNodes(node).flatMap(flatten),
];

// Nested REST tree -> the shared sub-item store. The root is held like any
// other node (no parent in `contents`, so branches never render it); the tree
// draws it through the `rootNode` slot.
const normalizeWorkOrderAssetTree = (
  tree: WorkOrderAssetTreeNode,
): NormalizedWorkOrderAssetTree => {
  const all = flatten(tree);

  return {
    nodes: R.indexBy(R.prop('id'), all.map(toElement)),
    contents: R.fromPairs(all.map(node => [node.id, toContents(node)])),
    details: R.fromPairs(all.map(node => [node.id, toDetail(node)])),
    isNewById: R.fromPairs(all.map(node => [node.id, Boolean(node.isNew)])),
  };
};

export default normalizeWorkOrderAssetTree;
